import { Dialog } from '@/components/ui/Dialog'
import { PlanSelector } from '@/components/billing/PlanSelector'
import { BillingDisabledBanner } from '@/components/billing/BillingDisabledBanner'
import type { Plan } from '@/api/types'
import { useTranslation } from 'react-i18next'

interface PlanSelectionDialogProps {
  open: boolean
  plans: Plan[]
  loading: boolean
  canChangePlan: boolean
  /** When false, plans render read-only: payments are still under development. */
  billingEnabled?: boolean
  currentPlanCode?: string | null
  cancelAtPeriodEnd?: boolean
  currentPeriodEnd?: string | null
  canceledAt?: string | null
  changingPlanId: number | null
  onClose: () => void
  onSelect: (plan: Plan) => void | Promise<void>
  onCancelPlan?: () => void | Promise<void>
  showCancelAction?: boolean
}

export function PlanSelectionDialog({
  open,
  plans,
  loading,
  canChangePlan,
  billingEnabled = true,
  currentPlanCode,
  cancelAtPeriodEnd = false,
  currentPeriodEnd = null,
  canceledAt = null,
  changingPlanId,
  onClose,
  onSelect,
  onCancelPlan,
  showCancelAction = false,
}: PlanSelectionDialogProps) {
  const { t, i18n } = useTranslation()
  const toAccessEndDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null
    const accessEndDate = new Date(dateStr)
    accessEndDate.setDate(accessEndDate.getDate() - 1)
    return accessEndDate.toISOString()
  }
  const resolvedPeriodEnd = (() => {
    if (currentPeriodEnd) return toAccessEndDate(currentPeriodEnd)
    if (!canceledAt) return null
    const fallbackDate = new Date(canceledAt)
    fallbackDate.setDate(fallbackDate.getDate() + 30)
    return toAccessEndDate(fallbackDate.toISOString())
  })()
  const formattedPeriodEnd = resolvedPeriodEnd
    ? new Date(resolvedPeriodEnd).toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : '—'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('billing.dialog.title', 'Plano e assinatura')}
      size="xl"
    >
      <div className="plan-dialog-content" style={{ paddingRight: 4 }}>
        {!billingEnabled ? (
          <div style={{ marginBottom: 20 }}>
            <BillingDisabledBanner compact />
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <PlanSelector
            plans={plans}
            loading={loading}
            currentPlanCode={currentPlanCode ?? null}
            selectedPlanCode={currentPlanCode ?? null}
            title={t('billing.dialog.selectorTitle', 'Escolha o plano ideal')}
            subtitle={t('billing.dialog.selectorSubtitle', 'Selecione o plano que melhor atende às necessidades do seu negócio e comece a transformar sua gestão hoje mesmo.')}
            note={billingEnabled && !canChangePlan ? t('billing.dialog.onlyOwner', 'Apenas o OWNER do workspace pode alterar o plano.') : undefined}
            onSelect={canChangePlan ? onSelect : undefined}
            renderAction={(plan, state) => {
              const isPaid = (plan.price_cents ?? 0) > 0
              const isCurrentPaid = state.isCurrent && isPaid && showCancelAction && canChangePlan
              const isCurrentCanceled = state.isCurrent && isPaid && cancelAtPeriodEnd

              if (isCurrentPaid) {
                return (
                  <button
                    disabled={changingPlanId !== null}
                    onClick={(event) => {
                      event.stopPropagation()
                      void onCancelPlan?.()
                    }}
                    style={{
                      width: '100%',
                      minHeight: 52,
                      borderRadius: 14,
                      border: '1px solid var(--color-danger-border)',
                      background: 'var(--color-danger-bg)',
                      color: 'var(--color-danger)',
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: changingPlanId !== null ? 'not-allowed' : 'pointer',
                      opacity: changingPlanId !== null ? 0.6 : 1,
                    }}
                  >
                    {t('billing.dialog.cancelPlan', 'Cancelar plano')}
                  </button>
                )
              }

              if (isCurrentCanceled) {
                return (
                  <div
                    style={{
                      width: '100%',
                      minHeight: 72,
                      borderRadius: 14,
                      border: '1px solid var(--color-danger-border)',
                      background: 'var(--color-danger-bg)',
                      color: 'var(--color-danger)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      padding: '12px 14px',
                      lineHeight: 1.4,
                      gap: 4,
                    }}
                  >
                    <strong style={{ fontSize: 13, fontWeight: 700 }}>
                      {t('billing.cancelScheduled', 'Cancelamento agendado')}
                    </strong>
                    <span style={{ fontSize: 12 }}>
                      {t('billing.planEndsOn', 'Você ainda tem um pouco de tempo, seu plano acaba em {{date}}', {
                        date: formattedPeriodEnd,
                      })}
                    </span>
                  </div>
                )
              }

              return (
                <button
                  disabled={!canChangePlan || state.isCurrent || changingPlanId !== null}
                  onClick={(event) => {
                    event.stopPropagation()
                    void onSelect(plan)
                  }}
                  style={{
                    width: '100%',
                    minHeight: 52,
                    borderRadius: 14,
                    border: `1px solid ${state.isCurrent ? 'rgba(34,211,238,.58)' : 'transparent'}`,
                    background: state.isCurrent
                      ? 'transparent'
                      : 'linear-gradient(90deg, #A855F7 0%, #22D3EE 100%)',
                    color: '#F8FBFF',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: !canChangePlan || state.isCurrent ? 'default' : 'pointer',
                    opacity: !canChangePlan && !state.isCurrent ? 0.45 : 1,
                  }}
                >
                  {state.isCurrent
                    ? t('billing.dialog.currentPlan', 'Plano atual')
                    : changingPlanId === plan.id
                      ? t('common.loading', 'Aguarde...')
                      : !billingEnabled
                        ? t('billing.disabled.action', 'Em breve')
                        : t('billing.dialog.select', 'Selecionar')}
                </button>
              )
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}
